<script lang="ts">
    /**
     * GridverseHeroSection - Gridverse 제품 페이지 히어로 (정식 출시)
     *
     * GV 안드로이드·iOS 양대 스토어 정식 출시(2026-08-27) → 다운로드 유도 톤.
     * JadEye HeroSection 패턴(좌 텍스트 + 우 폰 목업) 차용, accent = gold(#B49B50)로 JadEye(jade)와 구분.
     * primary CTA 2개 = Google Play / App Store **공식 배지**(JadEye 히어로와 같은 마크업·크기).
     *   🚨 2026-09-11 CEO 지적: 정식 출시 때 푸터는 배지로 바꾸고 여기만 골드 글자 버튼("…에서 받기")으로
     *      남아 있었다. 스토어 링크는 사이트 전체가 공식 배지다 — SeoTest::test_store_links_wear_the_official_badge.
     *   두 플랫폼을 동등하게 민다(같은 크기로 나란히) — 한쪽이 보조로 보이면 안 된다.
     * secondary = 컨셉 섹션으로 연결.
     */
    let isVisible = $state(false);

    $effect(() => {
        const t = setTimeout(() => {
            isVisible = true;
        }, 100);
        return () => clearTimeout(t);
    });

    function scrollToConcept(e: MouseEvent) {
        e.preventDefault();
        const el = document.querySelector('#gv-concept');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    }
</script>

<section class="gv-hero">
    <div class="hero-inner" class:visible={isVisible}>
        <!-- Left: Text Content -->
        <div class="hero-text">
            <span class="hero-eyebrow">정식 출시</span>
            <h1 class="hero-title">
                걸어서 개척하는<br />
                나만의 차원
            </h1>
            <p class="hero-subtitle">
                Gridverse는 현실 공간 위에 펼쳐지는 다차원 위치기반 AR 게임입니다.<br />
                걸어서 나만의 차원을 개척하고, 시즌의 끝 대동굿에 정성을 모아 함께 만들어가세요.
            </p>
            <div class="hero-store-badges">
                <a
                    href="https://play.google.com/store/apps/details?id=com.jadeye.gv"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="hero-badge-link"
                    aria-label="Google Play에서 다운로드 (새 창에서 열림)"
                >
                    <img src="/images/google-play-badge-ko.svg" alt="Google Play에서 다운로드" class="hero-badge" />
                </a>
                <a
                    href="https://apps.apple.com/kr/app/%EA%B7%B8%EB%A6%AC%EB%93%9C%EB%B2%84%EC%8A%A4/id6796878662"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="hero-badge-link"
                    aria-label="App Store에서 다운로드 (새 창에서 열림)"
                >
                    <img src="/images/app-store-badge-ko.svg" alt="App Store에서 다운로드" class="hero-badge" />
                </a>
            </div>
            <div class="hero-secondary-row">
                <a href="#gv-concept" class="hero-cta-secondary" onclick={scrollToConcept}>
                    <span>어떤 게임인가요?</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M12 5v14" /><path d="m19 12-7 7-7-7" />
                    </svg>
                </a>
            </div>
            <p class="hero-note">
                안드로이드와 iOS에서 정식 출시됐어요.<br />
                지금 바로 무료로 시작해요.
            </p>
        </div>

        <!-- Right: App Mockup -->
        <div class="hero-visual">
            <div class="phone-mockup">
                <div class="phone-screen">
                    <!-- LCP 요소. map.png 966KB → map.webp 164KB. picture 는 display:contents 라 레이아웃 불변. -->
                    <picture>
                        <source srcset="/images/gridverse/map.webp" type="image/webp" />
                        <img
                            src="/images/gridverse/map.png"
                            alt="Gridverse 메인 화면 - 헥사 영역 지도와 자원 노드"
                            class="phone-screenshot"
                            width="1080"
                            height="2340"
                            fetchpriority="high"
                        />
                    </picture>
                </div>
            </div>
        </div>
    </div>
</section>

<style>
    .gv-hero {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 6rem 1.5rem 2rem;
        background: linear-gradient(135deg, #f6f2e8 0%, #f1ebd9 30%, #fafbfa 100%);
        position: relative;
        overflow: hidden;
    }

    .gv-hero::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -30%;
        width: 80%;
        height: 80%;
        background: radial-gradient(circle, rgba(180, 155, 80, 0.1) 0%, transparent 70%);
        pointer-events: none;
    }

    .hero-inner {
        max-width: 72rem;
        margin: 0 auto;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 3rem;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .hero-inner.visible {
        opacity: 1;
        transform: translateY(0);
    }

    /* Text */
    .hero-text {
        text-align: center;
        max-width: 36rem;
    }

    .hero-eyebrow {
        display: inline-block;
        font-size: 0.82rem;
        font-weight: 700;
        letter-spacing: 0.12em;
        color: #8a7430;
        background: rgba(180, 155, 80, 0.14);
        padding: 0.4rem 1rem;
        border-radius: 999px;
        margin-bottom: 1.5rem;
    }

    .hero-title {
        font-size: clamp(2.2rem, 5vw, 3.5rem);
        font-weight: 700;
        line-height: 1.25;
        letter-spacing: -0.03em;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 1.5rem;
    }

    .hero-subtitle {
        font-size: 1.1rem;
        color: var(--landing-text-sub, #666666);
        line-height: 1.8;
        margin: 0 0 2.25rem;
    }

    /* 스토어 배지 2개 — JadEye 히어로(HeroSection)와 같은 마크업·크기.
       secondary('어떤 게임인가요?')는 아래 줄로 내려 위계를 나눈다(JadEye 와 같다). */
    .hero-store-badges {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        align-items: center;
        justify-content: center;
    }

    .hero-badge-link {
        display: inline-flex;
        align-items: center;
        transition:
            opacity 0.2s,
            transform 0.2s;
    }

    .hero-badge-link:hover {
        opacity: 0.9;
        transform: translateY(-2px);
    }

    .hero-badge {
        height: 3.25rem;
        object-fit: contain;
        display: block;
        filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.12));
    }

    .hero-secondary-row {
        display: flex;
        justify-content: center;
        margin-top: 0.5rem;
    }

    .hero-cta-secondary {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        padding: 0.6rem 1.4rem;
        color: var(--landing-text-sub, #666666);
        font-size: 0.95rem;
        font-weight: 500;
        text-decoration: none;
        transition: color 0.2s;
    }

    .hero-cta-secondary:hover {
        color: #8a7430;
    }

    .hero-note {
        font-size: 0.85rem;
        color: var(--landing-text-sub, #999999);
        margin: 1.75rem 0 0;
    }

    /* Phone Mockup */
    .hero-visual {
        flex-shrink: 0;
    }

    .phone-mockup {
        width: 240px;
        height: 480px;
        background: #1a1a1a;
        border-radius: 32px;
        padding: 12px;
        box-shadow:
            0 20px 60px rgba(0, 0, 0, 0.15),
            0 0 0 1px rgba(0, 0, 0, 0.05);
    }

    .phone-screen {
        width: 100%;
        height: 100%;
        background: #f6f2e8;
        border-radius: 22px;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    /* picture 가 박스를 만들지 않게 해서 img 가 그대로 .phone-screen(flex) 의 자식으로 남는다.
       이게 없으면 picture 가 flex 아이템이 되어 img 의 100% 기준이 무너진다. */
    picture {
        display: contents;
    }

    .phone-screenshot {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top;
    }

    /* Responsive */
    @media (min-width: 768px) {
        .gv-hero {
            padding: 6rem 2.5rem 2rem;
        }

        .hero-inner {
            flex-direction: row;
            align-items: center;
            gap: 4rem;
        }

        .hero-text {
            text-align: left;
            flex: 1;
        }

        .hero-store-badges,
        .hero-secondary-row {
            justify-content: flex-start;
        }

        .phone-mockup {
            width: 280px;
            height: 560px;
        }
    }

    @media (min-width: 1024px) {
        .gv-hero {
            padding: 6rem 3rem 2rem;
        }

        .phone-mockup {
            width: 300px;
            height: 600px;
        }
    }
</style>
